import VehicleOffering from '@carvana/merch-contracts/VehicleOffering';
import { useCallback, useContext } from 'react';
import {
  booleanFacets,
  listFacets,
  rangeFacets
} from '~/constants/common/facets';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import type {
  FacetBooleanKeys,
  FacetListKeys,
  FacetRangeKeys,
  FacetTag
} from '~/contracts/common';

type RemoveTagFunction = () => void;

function isBooleanFacet(facetKey: any): facetKey is FacetBooleanKeys {
  return booleanFacets.includes(facetKey);
}

function isListFacet(facetKey: any): facetKey is FacetListKeys {
  return listFacets.includes(facetKey);
}

function isRangeFacet(facetKey: any): facetKey is FacetRangeKeys {
  return rangeFacets.includes(facetKey);
}

export default function useRemoveFilterTag({
  facetKey,
  context
}: FacetTag): RemoveTagFunction {
  const {
    setAppliedFilters,
    updateAvailabilityFilter,
    updateBooleanFilter,
    updateListFilter,
    updateMakes,
    updateRangeFilter
  } = useContext(AppliedFiltersContext);

  return useCallback(() => {
    const isAvailability = facetKey === 'earliestDayToDeliveryOrPickup';
    if (isAvailability) {
      updateAvailabilityFilter({});
    }

    if (isBooleanFacet(facetKey)) {
      updateBooleanFilter({
        filterKey: facetKey,
        value: false
      });
    }

    if (
      facetKey === 'luxuryTypes' ||
      facetKey === 'seatMaterials' ||
      facetKey === 'sportTypes'
    ) {
      const keysToRemove = new Set(context.keys ?? [context.key]);
      setAppliedFilters(prev => {
        const current = prev[facetKey] ?? [];
        const next = current.filter(k => !keysToRemove.has(k));

        return { ...prev, [facetKey]: next.length > 0 ? next : undefined };
      });
    }

    if (isListFacet(facetKey)) {
      updateListFilter({ filterKey: facetKey, value: context.key });
    }

    if (isRangeFacet(facetKey)) {
      updateRangeFilter({
        filterKey: facetKey,
        value: {}
      });
    }

    if (facetKey === 'makes') {
      updateMakes({ name: context.key, ...context });
    }

    if (facetKey === 'offerings') {
      updateListFilter({
        filterKey: 'offerings',
        value: VehicleOffering.New
      });
    }

    if (facetKey === 'vehicleIds') {
      setAppliedFilters(prev => {
        const { vehicleIds: ignoredVehicleIds, ...appliedFilters } = prev;

        return appliedFilters;
      });
    }
  }, [
    context,
    facetKey,
    setAppliedFilters,
    updateAvailabilityFilter,
    updateBooleanFilter,
    updateListFilter,
    updateMakes,
    updateRangeFilter
  ]);
}
